import {useFormContext} from 'react-hook-form';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@common-web/components/ui/form';
import {Input} from '@common-web/components/ui/input';
import {Separator} from '@common-web/components/ui/separator';
import {Button} from '@common-web/components/ui/button';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {AddressFields} from '../components/AddressFields';
import {DateField} from '../components/DateField';
import {useState} from 'react';
import {Copy, Landmark} from 'lucide-react';
import {useRfcContext} from '../../RfcContext';

export function AchForm() {
  const {watch} = useFormContext();
  const {section125FormEnabled} = useRfcContext();

  const prefix = section125FormEnabled ? 'section125Form' : 'mecForm';

  const locations: Array<{locationName: string}> = watch(`${prefix}.groupInfo.locations`) || [];

  return (
    <div className='space-y-6'>
      <div className='flex items-center gap-2 mb-2'>
        <Landmark className='h-5 w-5 text-brand-blue' />
        <h3 className='text-lg font-semibold text-brand-navy'>ACH Authorization by Location</h3>
      </div>
      <p className='text-sm text-brand-gray'>
        Each location requires its own ACH banking details. You can copy ACH information from
        another location if they share the same bank account.
      </p>
      {locations.map((loc, index) => (
        <LocationAchCard
          key={index}
          index={index}
          basePath={`${prefix}.groupInfo.locations.${index}.achForm`}
          locationName={loc.locationName || `Location ${index + 1}`}
          allLocations={locations}
          formPrefix={prefix}
        />
      ))}
      {locations.length === 0 && (
        <p className='text-sm text-brand-gray'>
          No locations have been defined. Please go back to the Group Information step and add
          locations first.
        </p>
      )}
    </div>
  );
}

function LocationAchCard({
  index,
  basePath,
  locationName,
  allLocations,
  formPrefix,
}: {
  index: number;
  basePath: string;
  locationName: string;
  allLocations: Array<{locationName: string}>;
  formPrefix: string;
}) {
  const {control, watch, setValue, getValues} = useFormContext();
  const [routingNumberConfirm, setRoutingNumberConfirm] = useState('');
  const [accountNumberConfirm, setAccountNumberConfirm] = useState('');

  const routingNumber = watch(`${basePath}.achDetails.routingNumber`);
  const accountNumber = watch(`${basePath}.achDetails.accountNumber`);

  const copyFromLocation = (sourceIndex: string) => {
    const sourceAch = getValues(`${formPrefix}.groupInfo.locations.${sourceIndex}.achForm`);
    if (sourceAch) {
      setValue(basePath, sourceAch, {shouldDirty: true, shouldValidate: true});
      setRoutingNumberConfirm(sourceAch.achDetails?.routingNumber || '');
      setAccountNumberConfirm(sourceAch.achDetails?.accountNumber || '');
    }
  };

  const otherLocations = allLocations
    .map((loc, i) => ({index: i, name: loc.locationName || `Location ${i + 1}`}))
    .filter((loc) => loc.index !== index);

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardHeader className='bg-gray-50 border-b py-3'>
        <div className='flex items-center justify-between'>
          <CardTitle className='text-base font-semibold text-brand-navy'>{locationName}</CardTitle>
          {otherLocations.length > 0 && (
            <Select onValueChange={copyFromLocation}>
              <SelectTrigger className='w-auto max-w-[220px] h-8 text-xs border-gray-200'>
                <Copy className='h-3 w-3 mr-1' />
                <SelectValue placeholder='Copy from...' />
              </SelectTrigger>
              <SelectContent>
                {otherLocations.map((loc) => (
                  <SelectItem key={loc.index} value={String(loc.index)}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent className='space-y-4 pt-4'>
        <div>
          <div className='font-medium text-sm text-brand-navy mb-2'>Business Bank Account</div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <FormField
              control={control}
              name={`${basePath}.bankAccount.businessNameOnAccount`}
              render={({field}) => (
                <FormItem>
                  <FormLabel>Business Name on Account</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`${basePath}.bankAccount.accountHolderFirstName`}
              render={({field}) => (
                <FormItem>
                  <FormLabel>Account Holder First Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`${basePath}.bankAccount.accountHolderLastName`}
              render={({field}) => (
                <FormItem>
                  <FormLabel>Account Holder Last Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`${basePath}.bankAccount.contactPhone`}
              render={({field}) => (
                <FormItem>
                  <FormLabel>Contact Phone</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`${basePath}.bankAccount.contactEmail`}
              render={({field}) => (
                <FormItem>
                  <FormLabel>Contact Email</FormLabel>
                  <FormControl>
                    <Input type='email' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className='mt-3'>
            <div className='font-medium text-xs text-brand-gray mb-2'>Account Address</div>
            <AddressFields basePath={`${basePath}.bankAccount.address`} />
          </div>
        </div>
        <Separator />
        <div>
          <div className='font-medium text-sm text-brand-navy mb-2'>ACH Details</div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <FormField
              control={control}
              name={`${basePath}.achDetails.bankName`}
              render={({field}) => (
                <FormItem className='md:col-span-2'>
                  <FormLabel>Bank Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`${basePath}.achDetails.routingNumber`}
              render={({field}) => (
                <FormItem>
                  <FormLabel>Routing Number</FormLabel>
                  <FormControl>
                    <Input inputMode='numeric' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormItem>
              <FormLabel>Confirm Routing Number</FormLabel>
              <FormControl>
                <Input
                  inputMode='numeric'
                  value={routingNumberConfirm}
                  onChange={(e) => setRoutingNumberConfirm(e.target.value)}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  autoComplete='off'
                />
              </FormControl>
              {routingNumberConfirm && routingNumber !== routingNumberConfirm && (
                <p className='text-sm font-medium text-destructive'>Routing numbers must match</p>
              )}
            </FormItem>
            <FormField
              control={control}
              name={`${basePath}.achDetails.accountNumber`}
              render={({field}) => (
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormItem>
              <FormLabel>Confirm Account Number</FormLabel>
              <FormControl>
                <Input
                  value={accountNumberConfirm}
                  onChange={(e) => setAccountNumberConfirm(e.target.value)}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  autoComplete='off'
                />
              </FormControl>
              {accountNumberConfirm && accountNumber !== accountNumberConfirm && (
                <p className='text-sm font-medium text-destructive'>Account numbers must match</p>
              )}
            </FormItem>
          </div>
          <div className='mt-3'>
            <div className='font-medium text-xs text-brand-gray mb-2'>Bank Address</div>
            <AddressFields basePath={`${basePath}.achDetails.bankAddress`} />
          </div>
        </div>
        <Separator />
        <div>
          <div className='font-medium text-sm text-brand-navy mb-2'>Authorization & Agreement</div>
          <div className='text-sm text-muted-foreground mb-2'>
            I authorize Concierge Benefit Services, LLC (CBS) and Concierge Administrative Services,
            LLC (CAS) to initiate ACH transactions from/to my bank account at the financial
            institution indicated on this form. This authorization will remain in effect until CBS
            or CAS receives written notification from an authorized signer.
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <FormField
              control={control}
              name={`${basePath}.authorization.authorizedSignature`}
              render={({field}) => (
                <FormItem>
                  <FormLabel>Full legal name of authorized signer</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DateField name={`${basePath}.authorization.signatureDate`} label='Date Signed' />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
